import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    ShopAccountDetail,
    ShopAccountFilter,
} from '@/lib/api/shop-account-api'
import type { ApiResponse } from '@/lib/api/client'
import { shopAccountApi } from '@/lib/api/shop-account-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

export const shopAccountKeys = {
    all: ['shop-accounts'] as const,
    list: (f: ShopAccountFilter) => ['shop-accounts', f] as const,
    detail: (id: string) => ['shop-account', id] as const,
    byCustomer: (customerId: string) =>
        ['shop-account', 'customer', customerId] as const,
}

export function useShopAccounts(f: ShopAccountFilter) {
    return useQuery({
        queryKey: shopAccountKeys.list(f),
        queryFn: () => shopAccountApi.list(f),
        placeholderData: keepPreviousData,
    })
}

export function useShopAccount(id: string) {
    return useQuery({
        queryKey: shopAccountKeys.detail(id),
        queryFn: () => shopAccountApi.get(id),
    })
}

/** Tài khoản online của khách ERP (khối ở chi tiết khách); enabled theo quyền. */
export function useCustomerShopAccount(customerId: string, enabled: boolean) {
    return useQuery({
        queryKey: shopAccountKeys.byCustomer(customerId),
        queryFn: () => shopAccountApi.customerAccount(customerId),
        enabled,
    })
}

/** Đơn gần đây của tài khoản (màn chi tiết). */
export function useShopAccountOrders(id: string, perPage = 10) {
    return useQuery({
        queryKey: [...shopAccountKeys.detail(id), 'orders', perPage] as const,
        queryFn: () => shopAccountApi.orders(id, perPage),
    })
}

type Action = 'lock' | 'unlock' | 'revoke' | 'link'

const DONE: Record<Action, () => string> = {
    lock: () => m.shop_account_locked_toast(),
    unlock: () => m.shop_account_unlocked_toast(),
    revoke: () => m.shop_account_revoked_toast(),
    link: () => m.shop_account_linked_toast(),
}

// Mọi thao tác nhân viên trả lại chi tiết tài khoản: ghi thẳng vào cache chi
// tiết, làm mới danh sách.
export function useShopAccountAction(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (a: {
            action: Action
            customerId?: string | null
        }): Promise<ApiResponse<ShopAccountDetail>> => {
            switch (a.action) {
                case 'lock':
                    return shopAccountApi.lock(id)
                case 'unlock':
                    return shopAccountApi.unlock(id)
                case 'revoke':
                    return shopAccountApi.revokeSessions(id)
                default:
                    return shopAccountApi.setCustomerLink(
                        id,
                        a.customerId ?? null,
                    )
            }
        },
        onSuccess: (res, a) => {
            qc.setQueryData(shopAccountKeys.detail(id), res)
            void qc.invalidateQueries({ queryKey: shopAccountKeys.all })
            toast.success(DONE[a.action]())
        },
        onError: (err) => {
            const { title, description } = formatApiError(
                err,
                m.shop_account_action_failed(),
            )
            toast.error(title, { description })
        },
    })
}
